import type { FetchMoreOptions, OperationVariables } from '@apollo/client'
import type { UseLazyQueryReturn, UseQueryReturn } from '@vue/apollo-composable'
import type { UnwrapRef } from 'vue'

import type { TypedGraphQlError } from '#api/shared/@types/generated/backend/graphql-schema-types'

export type InferredUseQueryReturn<T> =
  | UseLazyQueryReturn<InferredResult<T>, InferredVariables<T>>
  | UseQueryReturn<InferredResult<T>, InferredVariables<T>>

type GetError<T> = (
  result: UnwrapRef<InferredUseQueryReturn<T>['result']>,
) => TypedGraphQlError[] | undefined
type GetNumberOf<T, N> = (result: UnwrapRef<InferredUseQueryReturn<T>['result']>) => N | undefined
type InferredResult<T> = T extends UseQueryReturn<infer U, any> ? U : any
type InferredVariables<T> = T extends UseQueryReturn<any, infer C> ? C : any

export const useFetchMoreWrapper = <
  T extends InferredUseQueryReturn<T>,
  N extends number | Record<string, number>,
>(
  query: () => T,
  updateQuery: FetchMoreOptions['updateQuery'],
  getCount: GetNumberOf<T, N>,
  getTotal: GetNumberOf<T, N>,
  errorPrefix: string,
  getErrorAsData?: GetError<T>,
) => {
  const { addToastError } = useToaster()

  const payload = query()

  const count = computed(() => getCount(payload.result.value) ?? 0)
  const total = computed(() => getTotal(payload.result.value) ?? 0)
  const error = computed(() => {
    if (getErrorAsData) return getErrorAsData(payload.result.value)
  })

  const handleError = (error: unknown) => {
    addToastError({
      error,
      prefix: errorPrefix,
      tracking: {
        topicKey: 'load_more_failed',
      },
    })
  }

  const inflightByKey = ref<Record<string, number>>({})

  const loadingMore = computed(() =>
    Object.values(inflightByKey.value).some((inflight) => inflight > 0),
  )
  const isLoadingMore = (key = 'default') => (inflightByKey.value[key] ?? 0) > 0

  const invokeFetchMore = async (variables: OperationVariables = {}, key = 'default') => {
    if (total.value === 0) return

    inflightByKey.value[key] = (inflightByKey.value[key] ?? 0) + 1
    try {
      await payload.fetchMore({
        updateQuery,
        variables: variables as any,
      })
    } catch (error) {
      handleError(error)
    } finally {
      inflightByKey.value[key]--
    }
  }

  const invokeRefetch = async (variables: OperationVariables = {}) => {
    try {
      await payload.fetchMore({
        updateQuery,
        variables: variables as any,
      })
    } catch (error) {
      handleError(error)
    }
  }

  payload.onError(handleError)

  return {
    ...payload,
    count,
    error,
    fetchMore: invokeFetchMore,
    isLoadingMore,
    loadingMore,
    refetch: invokeRefetch,
    total,
  }
}
